import React, { useState, useEffect } from 'react';
import {
  TextField,
  IconButton,
  InputAdornment,
  Box,
  Chip,
  Tooltip,
  CircularProgress,
  SxProps,
  Theme
} from '@mui/material';
import {
  Clear as ClearIcon,
  ZoomIn as ZoomInIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { BlobticZoomPopup } from './BlobticZoomPopup';
import { useZoomField } from '../hooks/useBlobticZoom';
import { ZoomData } from '../services/blobticZoomService';
import { useLanguage } from '../contexts/LanguageContext';

interface ZoomFieldProps {
  zoomKey: string;
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string, selectedData?: ZoomData) => void;
  displayField?: string; // Champ à afficher dans le TextField
  valueField?: string; // Champ à utiliser comme valeur
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  helperText?: string;
  error?: boolean;
  onZoomOpen?: () => void;
  onZoomClose?: () => void;
  showSelectedInfo?: boolean; // Afficher les informations de l'élément sélectionné
  className?: string;
  sx?: SxProps<Theme>; // Style personnalisé
  zoomDisabled?: boolean; // Désactiver le bouton zoom
  zoomDisabledMessage?: string; // Message quand le zoom est désactivé
  dynamicParams?: Record<string, string>; // Paramètres dynamiques pour le zoom
}

export const ZoomField: React.FC<ZoomFieldProps> = ({
  zoomKey,
  label,
  placeholder,
  value,
  onChange,
  displayField,
  valueField,
  required = false,
  disabled = false,
  fullWidth = false,
  size = 'medium',
  variant = 'outlined',
  helperText,
  error = false,
  onZoomOpen,
  onZoomClose,
  showSelectedInfo = false,
  className,
  sx,
  zoomDisabled = false,
  zoomDisabledMessage,
  dynamicParams
}) => {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isZoomOpen, setIsZoomOpen] = useState(false);

  const {
    selectedData,
    displayValue,
    fieldConfig,
    initializeField,
    getValue,
    clearSelection
  } = useZoomField(zoomKey, (data) => {
    if (onChange) {
      onChange(getValue(), data);
    }
  });

  // Initialiser le champ au montage
  useEffect(() => {
    initializeField(displayField, valueField);
  }, [initializeField, displayField, valueField]);

  // Synchroniser avec la valeur externe
  useEffect(() => {
    if (value !== undefined && value !== getValue()) {
      // Si la valeur externe change, on peut vouloir effacer la sélection
      // ou chercher la correspondance dans les données
      if (!value) {
        clearSelection();
      }
    }
  }, [value, getValue, clearSelection]);

  const handleZoomClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || zoomDisabled) return;

    setIsLoading(true);
    setValidationErrors([]);

    try {
      // Notifier l'ouverture du zoom
      if (onZoomOpen) {
        onZoomOpen();
      }

      setIsZoomOpen(true);
      console.log(`Ouvrir le zoom: ${zoomKey}`);

    } catch (error) {
      console.error('Erreur lors de l\'ouverture du zoom:', error);
      setValidationErrors([t('zoom.error.validation')]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    clearSelection();
    if (onChange) {
      onChange('', undefined);
    }
  };

  const getDisplayValue = () => {
    if (value !== undefined) {
      // Si une valeur externe est fournie, on l'utilise
      return value;
    }
    // Sinon, on utilise la valeur sélectionnée
    return displayValue;
  };

  const getHelperText = () => {
    if (validationErrors.length > 0) {
      return validationErrors.join(', ');
    }
    if (helperText) {
      return helperText;
    }
    if (selectedData && showSelectedInfo) {
      const info = Object.entries(selectedData)
        .filter(([key]) => key !== fieldConfig.valueField && key !== fieldConfig.displayField)
        .map(([key, val]) => `${key}: ${val}`)
        .join(', ');
      return info || undefined;
    }
    return undefined;
  };

  const hasError = error || validationErrors.length > 0;

  return (
    <Box className={className} sx={sx}>
      <TextField
        label={label}
        placeholder={placeholder}
        value={getDisplayValue()}
        onChange={(e) => {
          if (onChange) {
            onChange(e.target.value);
          }
        }}
        required={required}
        disabled={disabled}
        fullWidth={fullWidth}
        size={size}
        variant={variant}
        error={hasError}
        helperText={getHelperText()}
        autoComplete="off"
        InputProps={{
          readOnly: true, // Empêche la saisie directe
          startAdornment: (
            <InputAdornment position="start" sx={{ marginInlineEnd: 1, marginInlineStart: 0.5 }}>
              <BusinessIcon color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  {selectedData && (
                    <Tooltip title={t('zoom.tooltip.clear')}>
                      <IconButton
                        size="small"
                        onClick={handleClear}
                        disabled={disabled}
                        sx={{ mr: 0.5 }}
                      >
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={zoomDisabled ? (zoomDisabledMessage || t('zoom.tooltip.disabled')) : t('zoom.tooltip.open', zoomKey)}>
                    <IconButton
                      aria-label="ouvrir le zoom"
                      onClick={handleZoomClick}
                      disabled={disabled || zoomDisabled}
                      edge="end"
                    >
                      <ZoomInIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </InputAdornment>
          )
        }}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            transition: 'all 0.3s ease-in-out',
            '&:hover fieldset': {
              borderColor: 'primary.main',
              borderWidth: '2px',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
              borderWidth: '2px',
              boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}1A`, // 10% opacity
            },
          },
          ...sx
        }}
      />

      {/* Affichage des informations supplémentaires si demandé */}
      {showSelectedInfo && selectedData && (
        <Box sx={{ mt: 1 }}>
          {Object.entries(selectedData)
            .filter(([key]) => key !== fieldConfig.valueField && key !== fieldConfig.displayField)
            .map(([key, value]) => (
              <Chip
                key={key}
                label={`${key}: ${value}`}
                size="small"
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
        </Box>
      )}
      {/* Popup de Zoom */}
      {isZoomOpen && (
        <BlobticZoomPopup
          open={isZoomOpen}
          zoomKey={zoomKey}
          dynamicParams={dynamicParams}
          onClose={() => {
            setIsZoomOpen(false);
            if (onZoomClose) onZoomClose();
          }}
          onSelect={(data) => {
            if (onChange) {
              // Update internal state via hook logic if simpler, or just direct callback
              // The hook 'initializeField' / 'getValue' might need update, 
              // but for now we trust the parent to handle onChange(value, data)
              // and pass the new value back in. 
              // BUT ZoomField hook logic (useBlobticZoom) manages internal selectedData.
              // We should probably rely on the parent for the value, but the hook manages 'selectedData' for display.
              // We need to update the hook's state? 
              // 'useZoomField' doesn't seem to expose a 'setSelectedData'.
              // It exposes 'getValue'.
              // Let's rely on the onSelect prop of this component to propagate data.
              // And we assume the parent updates 'value' prop.
              // However, to show the 'Chip' info, we need 'selectedData'.
              // We might need to manually set it if the hook doesn't auto-fetch on ID change (which it should).
              onChange(data[valueField || fieldConfig.valueField || 'id'], data);
            }
            setIsZoomOpen(false);
            if (onZoomClose) onZoomClose();
          }}
        />
      )}
    </Box>
  );
};
